import React, { useMemo } from "react";
import useSpacing from '../../core/hooks/useSpacing';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import useCharacterStyle from '../../core/hooks/useCharacterStyle';
import { getTextPositionClasses } from "../../core/utils/characterStyleUtils";
import { transformColor } from "../../core/utils/generalUtils";

function getOffsetCopyConfig(characterStyle) {
  const dir = characterStyle?.text_offset_copy;
  if (!dir || dir === "none") return null;

  const distance = Number(characterStyle?.text_offset_copy_distance);
  const d = Number.isFinite(distance) ? distance : 7;
  const scaleRaw = Number(characterStyle?.text_offset_copy_scale);
  const scale = Number.isFinite(scaleRaw) ? Math.max(0.1, Math.min(1, scaleRaw)) : 1;

  const map = {
    right: [d, 0],
    left: [-d, 0],
    top: [0, -d],
    bottom: [0, d],
    "top-right": [d, -d],
    "top-left": [-d, -d],
    "bottom-right": [d, d],
    "bottom-left": [-d, d],
  };
  const [dx, dy] = map[dir] || [d, d];

  const textHex = transformColor(characterStyle?.text_color || "#111827", 100, "hex");
  const copyColor = characterStyle?.text_offset_copy_color
    ? transformColor(characterStyle.text_offset_copy_color, 100, "rgba")
    : transformColor(textHex, 35, "rgba");

  const connect = Boolean(characterStyle?.text_offset_copy_connect);
  return { dx, dy, scale, copyColor, connect };
}

function buildOffsetTrailLayers(offsetCopy) {
  if (!offsetCopy) return [];
  const { dx, dy, scale, connect } = offsetCopy;
  if (!connect) return [{ dx, dy, scale }];

  const dist = Math.hypot(dx, dy);
  // Connected trail density uses a logistic function of distance + size so:
  // - Shorter distances get more repetitions (denser trail for small offsets)
  // - Smaller sizes (scale closer to 0.1) get more repetitions
  //
  // x = (1 / max(distance, 3)) * max((1 - size) * 10, 1)
  // densityMultiplier = 1 + 10 * sigmoid(2 * (x - 1))   // ~1..11
  const distance = Math.max(dist, 0);
  const size = Number.isFinite(scale) ? scale : 1;
  const x = (1 / Math.max(distance, 3)) * Math.max((1 - size) * 10, 1);
  const sigmoid = 1 / (1 + Math.exp(-2 * (x - 1)));
  const densityMultiplier = 1 + 10 * sigmoid;

  const baseStepPx = dist >= 140 ? 1 : dist >= 60 ? 1.5 : 2;
  const stepPx = baseStepPx / densityMultiplier;
  // Extra repetitions for very short offsets or small sizes.
  const bonusConnections = dist < 4 || size < 0.7 ? 12 : 0;
  const steps = Math.max(2, Math.min(520, Math.ceil(dist / stepPx) + bonusConnections));
  const layers = [];
  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    layers.push({
      dx: dx * t,
      dy: dy * t,
      scale: 1 + (scale - 1) * t,
    });
  }
  return layers;
}

/**
 * Compute an explicit bold font-weight that is visibly heavier than the given
 * custom weight.  Follows the same step logic as the CSS `bolder` keyword but
 * produces a concrete numeric value so every browser/font renders it
 * consistently.
 *
 *   base ≤ 300  → 700
 *   base 400-500 → 700
 *   base 600-700 → 900
 *   base ≥ 800  → 900 (max possible)
 */
function computeBoldWeight(customWeight) {
  const w = Number(customWeight);
  if (Number.isNaN(w)) return null;
  const bold = Math.min(Math.max(w + 300, 700), 900);
  // Only useful if it's actually heavier than the base weight
  return bold > w ? bold : null;
}

/**
 * When a custom font-weight is active, inject an explicit heavier weight on
 * every <strong> and <b> tag inside the rendered HTML so that markdown **bold**
 * text is visually bolder than the surrounding content.
 */
function injectBoldWeight(html, charStyle) {
  if (!html) return html;

  const enabled = charStyle?.font_custom_weight_enabled;
  const weight = charStyle?.font_custom_weight;
  if (!enabled || weight == null) return html;

  const boldWeight = computeBoldWeight(weight);
  if (boldWeight == null) return html;

  // Inject inline font-weight on <strong …> and <b …> / <b> tags.
  // The regex preserves any existing attributes on the tag.
  return html
    .replace(/<strong([^>]*)>/g, `<strong$1 style="font-weight:${boldWeight}">`)
    .replace(/<b([\s>])/g, `<b style="font-weight:${boldWeight}"$1`);
}

const TextItem = ({ item, returnDiv = true, ignoreSpacing = false }) => {
  const { combinedClasses } = useSpacing(item, ignoreSpacing);
  const { styleClasses: baseClasses, inlineStyles: baseStyles } =
    useBaseStyle(item);
  const { styleClasses: charClasses, inlineStyles: charStyles } =
    useCharacterStyle(item);

  // Resolve character style object (API may use either key)
  const charStyle = item?.character_style || item?.characterStyle;

  // When a custom font-weight is set on the container, <strong>/<b> elements
  // need an explicitly heavier weight so markdown **bold** remains visible.
  const htmlContent = useMemo(
    () => injectBoldWeight(item?.rendered_content, charStyle),
    [item?.rendered_content, charStyle?.font_custom_weight_enabled, charStyle?.font_custom_weight],
  );

  // Handle null/undefined items gracefully
  if (!item || !htmlContent) {
    return null;
  }

  // Tailwind preflight resets margins for elements like <p>, so markdown content can
  // look like it has "no blank line" between paragraphs. Explicitly restore
  // paragraph spacing (and handle double <br> as a safety net for blank lines).
  const richTextSpacingClasses = [
    // Markdown paragraphs
    "[&_p]:m-0",
    "[&_p+_p]:mt-4",
    // Markdown lists (Tailwind preflight removes list styles/padding)
    "[&_ul]:list-disc",
    "[&_ol]:list-decimal",
    "[&_ul]:pl-6",
    "[&_ol]:pl-6",
    "[&_ul]:m-0",
    "[&_ol]:m-0",
    "[&_li]:m-0",
    "[&_li+_li]:mt-1",
    // Blank line created via <br><br>
    "[&_br+br]:block",
    "[&_br+br]:mt-4",
  ].join(" ");

  // The character-style hook returns container positioning classes built for a
  // flex-row parent (e.g. `flex justify-end items-center text-right`).  Our
  // Text wrapper is `flex flex-col`, so those `items-*` end up controlling the
  // *horizontal* (cross) axis and visually push paragraphs to a side that does
  // not match the user's selection — making text alignment appear stuck
  // (typically to the right whenever a "bottom-*" position is chosen).
  //
  // Strip those layout-oriented classes from charClasses and re-derive the
  // pure text-align class from `text_position` so it can be applied directly
  // to the content element where it actually affects each `<p>`.
  const textPosition =
    charStyle?.text_position || "middle-full";
  const textAlignClass = getTextPositionClasses(textPosition, false);

  // Strip layout-oriented classes (flex container + flex alignment + text-align)
  // produced by getCharacterStyleClasses for a row-flex parent. We re-apply the
  // pure text-align (textAlignClass) on the content element instead, so each
  // <p> aligns correctly within the flex-col wrapper.
  // NOTE: the previous regex used `justify-`/`items-` with a `$` anchor, which
  // only matched the literal strings and let `justify-center`, `items-end`,
  // etc. through. That caused <p> children to shrink/center on the cross axis
  // and made the alignment selection appear stuck (typically to the middle).
  const cleanCharClasses = charClasses
    .split(" ")
    .filter(
      (c) =>
        !/^(flex|justify-\S+|items-\S+|text-left|text-right|text-center|text-justify)$/.test(
          c,
        ),
    )
    .join(" ");

  let finalClasses = `${combinedClasses} ${baseClasses} ${cleanCharClasses}`.trim();
  // Normalize white/color classes so Tailwind hover utilities work
  finalClasses = finalClasses
    .split(" ")
    .map((c) =>
      c
        .replace(/^hover:text-white(-\d+)?$/, "hover:text-white")
        .replace(/^text-white(-\d+)?$/, "text-white"),
    )
    .join(" ");

  // Merge inline styles (keep color for proper styling), but *combine* text-shadow
  // instead of letting CharacterStyle overwrite BaseStyle.
  const mergedTextShadow = [baseStyles?.textShadow, charStyles?.textShadow]
    .filter(Boolean)
    .join(", ");
  const finalStyles = {
    ...baseStyles,
    ...charStyles,
    ...(mergedTextShadow ? { textShadow: mergedTextShadow } : {}),
  };

  const offsetCopy = getOffsetCopyConfig(charStyle);
  const offsetLayers = buildOffsetTrailLayers(offsetCopy);

  const wrapperClassName = returnDiv
    ? `relative flex flex-col space-y-4 ${richTextSpacingClasses} ${finalClasses}`
    : `relative inline-flex items-center align-middle ${finalClasses}`;
  const whiteSpace = returnDiv ? "pre-wrap" : "nowrap";

  const foreground = returnDiv ? (
    <div
      className={`relative z-[1] w-full ${textAlignClass}`.trim()}
      style={{ whiteSpace, ...finalStyles }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  ) : (
    <span
      className={`relative z-[1] ${textAlignClass}`.trim()}
      style={{ whiteSpace, ...finalStyles }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );

  const copyLayer = offsetCopy ? (
    <div className="absolute left-0 top-0 z-0 w-full pointer-events-none select-none">
      {offsetLayers.map((layer, idx) =>
        returnDiv ? (
          <div
            key={idx}
            aria-hidden="true"
            className="absolute left-0 top-0 w-full"
            style={{
              whiteSpace,
              ...finalStyles,
              color: offsetCopy.copyColor,
              textShadow: "none",
              // Ensure the offset layer does NOT inherit text border/stroke from BaseStyle.
              "--pc-text-stroke-width": "0px",
              "--pc-text-stroke-color": "transparent",
              backgroundColor: "transparent",
              transform: `translate(${layer.dx}px, ${layer.dy}px) scale(${layer.scale})`,
              transformOrigin: "top center",
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : (
          <span
            key={idx}
            aria-hidden="true"
            className="absolute left-0 top-0"
            style={{
              whiteSpace,
              ...finalStyles,
              color: offsetCopy.copyColor,
              textShadow: "none",
              // Ensure the offset layer does NOT inherit text border/stroke from BaseStyle.
              "--pc-text-stroke-width": "0px",
              "--pc-text-stroke-color": "transparent",
              backgroundColor: "transparent",
              transform: `translate(${layer.dx}px, ${layer.dy}px) scale(${layer.scale})`,
              transformOrigin: "top center",
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ),
      )}
    </div>
  ) : null;

  const contentElement = offsetCopy ? (
    <div className={wrapperClassName}>
      {copyLayer}
      {foreground}
    </div>
  ) : (
    // No offset copy: keep the old DOM shape (single element) to minimize layout differences.
    returnDiv ? (
      <div
        className={`flex flex-col space-y-4 ${richTextSpacingClasses} ${finalClasses} ${textAlignClass}`}
        style={{ whiteSpace, ...finalStyles }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    ) : (
      <span
        className={`inline-flex items-center align-middle ${finalClasses} ${textAlignClass}`}
        style={{ whiteSpace, ...finalStyles }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    )
  );

  // Avoid an extra flex wrapper around the element that carries spacing classes.
  // A stretched flex item + horizontal margins can visually "shift" content to the right
  // in some layouts. Returning the content element directly keeps margins symmetric and centered.
  return contentElement;
};

export default TextItem;
